import * as reduxApi from 'src/utils/redux-api-v2';
import type {Dispatch, ThunkAction} from 'src/reducers';
import {ANALYTICS_API_V4} from 'src/utils/analytics-api-migration';
import type {ApiOptions, ApiPostOptions} from 'src/utils/redux-api';
import type {
  CandidateSourcePerformanceFilters,
  SavedDashboardList,
  SelectedCandidateSourceColumnsData,
} from 'src/types/analytics-genesis';

import {
  allSourcesTableColumnsQuery,
  subSourcesTableColumnsQuery,
  agencyPerformanceTableColumnsQuery,
} from 'src/utils/analytics-genesis';

import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';

import {thunkify as flow} from 'src/utils/thunks';

import {fetching, key} from 'src/utils/redux';


const analyticsOptions: ApiOptions = {apiPath: ANALYTICS_API_V4};
const analyticsApiOptions: ApiPostOptions = {apiPath: ANALYTICS_API_V4};

export const SET_CURRENT_DASHBOARD_ID =
  'analytics-candidate-source-performance/set-current-dashboard';
export const SET_SELECTED_DATE_RANGE =
  'analytics-candidate-source-performance/set-selected-date-range';
export const CLEAR_SELECTED_FILTERS =
  'analytics-candidate-source-performance/clear-selected-filters';
export const RECEIVE_SAVED_DASHBOARDS =
  'analytics-candidate-source-performance/receive-saved-dashboards';
export const SET_SELECTED_CANDIDATE_SOURCE_FILTERS =
  'analytics-candidate-source-performance/set-selected-candidate-source-filters';

export const RECEIVE_ALL_SOURCE_TABLE_COLUMNS =
  'analytics-candidate-source-performance/receive-all-source-table-columns';
export const RECEIVE_SUB_SOURCE_TABLE_COLUMNS =
  'analytics-candidate-source-performance/receive-sub-source-table-columns';
export const RECEIVE_AGENCY_PERFORMANCE_TABLE_COLUMNS =
  'analytics-candidate-source-performance/receive-agency-performance-table-columns';

type SetCurrentDashboardIdAction = {
  type: 'analytics-candidate-source-performance/set-current-dashboard';
  payload: string | null | undefined;
};

type SetDateRangeAction = {
  type: 'analytics-candidate-source-performance/set-selected-date-range';
  payload: DateRangeWithTimezone;
};

type ReceiveSavedDashboardsAction = {
  type: 'analytics-candidate-source-performance/receive-saved-dashboards';
  payload: SavedDashboardList;
};

type ClearSelectedFiltersAction = {
  type: 'analytics-candidate-source-performance/clear-selected-filters';
};

type SetSelectedCandidateSourceFiltersAction = {
  type: 'analytics-candidate-source-performance/set-selected-candidate-source-filters';
  payload: CandidateSourcePerformanceFilters;
};

type ReceiveAllSourcesTableColumnAction = {
  type: 'analytics-candidate-source-performance/receive-all-source-table-columns';
  payload: SelectedCandidateSourceColumnsData;
};

type ReceiveAgencyPerformanceTableColumnAction = {
  type: 'analytics-candidate-source-performance/receive-agency-performance-table-columns';
  payload: SelectedCandidateSourceColumnsData;
};

type ReceiveSubSourceTableColumnAction = {
  type: 'analytics-candidate-source-performance/receive-sub-source-table-columns';
  payload: SelectedCandidateSourceColumnsData;
};

export type AnalyticsCandidateSourcePerformanceActions =
  | SetCurrentDashboardIdAction
  | SetDateRangeAction
  | ReceiveSavedDashboardsAction
  | ClearSelectedFiltersAction
  | SetSelectedCandidateSourceFiltersAction
  | ReceiveAllSourcesTableColumnAction
  | ReceiveAgencyPerformanceTableColumnAction
  | ReceiveSubSourceTableColumnAction;

export const setCurrentDashboardId = (
  id?: string | null,
): SetCurrentDashboardIdAction => ({
  type: SET_CURRENT_DASHBOARD_ID,
  payload: id,
});

export const setDateRange = (
  dateRange: DateRangeWithTimezone,
): SetDateRangeAction => ({
  type: SET_SELECTED_DATE_RANGE,
  payload: dateRange,
});

export const receiveSavedDashboards = (
  filters: SavedDashboardList,
): ReceiveSavedDashboardsAction => ({
  type: RECEIVE_SAVED_DASHBOARDS,
  payload: filters,
});

export const setSelectedCandidateSourceFilters = (
  filters: CandidateSourcePerformanceFilters,
): SetSelectedCandidateSourceFiltersAction => ({
  type: SET_SELECTED_CANDIDATE_SOURCE_FILTERS,
  payload: filters,
});

export const clearSelectedFilters = (): ClearSelectedFiltersAction => ({
  type: CLEAR_SELECTED_FILTERS,
});

const receiveAllSourcesTableColumnSelection = (
  payload: SelectedCandidateSourceColumnsData,
): ReceiveAllSourcesTableColumnAction => ({
  type: RECEIVE_ALL_SOURCE_TABLE_COLUMNS,
  payload,
});

const receiveAgencyPerformanceTableColumnSelection = (
  payload: SelectedCandidateSourceColumnsData,
): ReceiveAgencyPerformanceTableColumnAction => ({
  type: RECEIVE_AGENCY_PERFORMANCE_TABLE_COLUMNS,
  payload,
});

const receiveSubSourceTableColumnSelection = (
  payload: SelectedCandidateSourceColumnsData,
): ReceiveSubSourceTableColumnAction => ({
  type: RECEIVE_SUB_SOURCE_TABLE_COLUMNS,
  payload,
});

export const getCandidateSourcePerformanceDashboards: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.get(
          '/candidate-nps/save-filter',
          {
            product: 'crm',
            dashboard: 'candidate-source',
          },
          analyticsOptions,
        ),
      );
      const {
        filters,
      }: {
        filters: SavedDashboardList;
      } = response;
      await dispatch(receiveSavedDashboards(filters));

      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {id = '', filters = {}} = defaultDashboard;
        const {
          // @ts-expect-error - TS2339 - Property 'job_id_title' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_id_title = [],
          // @ts-expect-error - TS2339 - Property 'job_department' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_department = [],
          // @ts-expect-error - TS2339 - Property 'job_location' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_location = [],
        } = filters;
        await dispatch(setCurrentDashboardId(id));
        await dispatch(
          setSelectedCandidateSourceFilters({
            job_id_title,
            job_department,
            job_location,
          }),
        );
      }
    } catch (error: any) {
      console.error('Error fetching saved dashboard filter:', error);
    }
  };

export const getAllSourceTableFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getAllSourceTableFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedCandidateSourceColumnsData = await dispatch(
      reduxApi.get(
        '/candidate-source/column-selection',
        allSourcesTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receiveAllSourcesTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching sources overview column filter:', error);
  }
});

export const getSubSourceTableFilterColumn: () => ThunkAction<unknown> = flow(
  key(() => 'getSubSourceTableFilterColumn'),
  fetching(),
)(() => async (dispatch: Dispatch) => {
  try {
    const payload: SelectedCandidateSourceColumnsData = await dispatch(
      reduxApi.get(
        '/candidate-source/column-selection',
        subSourcesTableColumnsQuery,
        analyticsOptions,
      ),
    );
    dispatch(receiveSubSourceTableColumnSelection(payload));
  } catch (error: any) {
    console.error('Error fetching sub sources overview column filter:', error);
  }
});

export const getAgencyPerformanceTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getAgencyPerformanceTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload: SelectedCandidateSourceColumnsData = await dispatch(
        reduxApi.get(
          '/candidate-source/column-selection',
          agencyPerformanceTableColumnsQuery,
          analyticsOptions,
        ),
      );
      dispatch(receiveAgencyPerformanceTableColumnSelection(payload));
    } catch (error: any) {
      console.error(
        'Error fetching agency performance overview column filter:',
        error,
      );
    }
  });

export const saveCandidateSourceFilterColumns: (
  columnData: SelectedCandidateSourceColumnsData,
) => ThunkAction<unknown> = flow()(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(
      reduxApi.post(
        '/candidate-source/column-selection',
        columnData,
        {},
        analyticsApiOptions,
      ),
    ),
);
